import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Portal, Loader } from '@repletoryreach/sdk-ui';
import { InputWrapper } from '@repletoryreach/sdk-ui/inputs';
import { computePosition } from '@repletoryreach/sdk-ui/overlays';
import { useOnClickOutside } from '@repletoryreach/sdk-utils/hooks';
import { settings, isApiError } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';

interface Props {
  providerCode: string;
  field: settings.IntegrationFieldSchema;
  /** Every field's label by key, for "Fill in AWS region and Access key ID first". */
  labels: Record<string, string>;
  /** The whole draft: the list is asked for with the values just typed, saved or not. */
  config: Record<string, string>;
  value: string;
  onChange: (v: string) => void;
}

type ListState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; options: settings.IntegrationFieldOption[]; note: string | null }
  | { status: 'error'; message: string };

type Row =
  | { kind: 'typed'; value: string }
  | { kind: 'option'; option: settings.IntegrationFieldOption };

const DEBOUNCE_MS = 600;

function matches(o: settings.IntegrationFieldOption, q: string): boolean {
  if (!q) return true;
  return o.value.toLowerCase().includes(q)
    || o.label.toLowerCase().includes(q)
    || (o.hint ?? '').toLowerCase().includes(q)
    || (o.group ?? '').toLowerCase().includes(q);
}

/**
 * A `select_remote` field: a searchable pick-list whose choices the settings
 * service works out from the draft values on the form, before anything is
 * saved, so the list reflects the region and keys the operator has just typed.
 *
 * The box always holds the VALUE (the model id, the region), which is what is
 * saved and what an operator recognises; the list is a helper over it. So
 * whatever is typed is kept ("use what I typed"), a brand-new model can be
 * entered before the list knows it, and a saved value that is not in the list
 * is shown as such rather than silently replaced.
 *
 * Keyboard: typing filters and opens; Arrow Down opens or moves; Enter takes
 * the highlighted row (or keeps the typed text); Escape closes the list and
 * stops there, so the drawer stays open; Tab closes and moves on.
 */
export function RemoteSelect({ providerCode, field, labels, config, value, onChange }: Props) {
  const t = useT();
  const listId = useId();

  const deps = useMemo(() => field.depends_on ?? [], [field.depends_on]);
  const reads = useMemo(() => field.also_reads ?? [], [field.also_reads]);
  const missing = deps.filter((k) => !(config[k] ?? '').trim());
  const ready = missing.length === 0;
  // The values the list is built from. A change here reloads it after a
  // pause, so a region typed letter by letter does not fire a call a letter.
  const watched = JSON.stringify([...deps, ...reads].map((k) => (config[k] ?? '').trim()));
  const configRef = useRef(config);
  configRef.current = config;

  const [list, setList] = useState<ListState>({ status: 'idle' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!ready) { setList({ status: 'idle' }); return; }
    let cancelled = false;
    setList({ status: 'loading' });
    const timer = setTimeout(async () => {
      try {
        const res = await settings.integrationFieldOptions(providerCode, field.key, { config: configRef.current });
        if (!cancelled) setList({ status: 'ready', options: res.data.options ?? [], note: res.data.note ?? null });
      } catch (e) {
        if (cancelled) return;
        const message = isApiError(e) || e instanceof Error ? e.message : t('common.unknownError');
        setList({ status: 'error', message });
      }
    }, deps.length === 0 ? 0 : DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // `watched` stands in for the dependency values; `t` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerCode, field.key, ready, watched, attempt]);

  const options = list.status === 'ready' ? list.options : [];
  const selected = options.find((o) => o.value === value) ?? null;

  // ── the open list ─────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [coords, setCoords] = useState<{ x: number; y: number; width: number } | null>(null);
  const [menuEl, setMenuEl] = useState<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => options.filter((o) => matches(o, q)), [options, q]);
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    const typed = value.trim();
    if (typed && !options.some((o) => o.value === typed)) out.push({ kind: 'typed', value: typed });
    for (const o of visible) out.push({ kind: 'option', option: o });
    return out;
  }, [value, options, visible]);

  // Group headings in the order the service listed them.
  const grouped = useMemo(() => {
    const groups: { name: string | null; rows: { row: Row; index: number }[] }[] = [];
    rows.forEach((row, index) => {
      const name = row.kind === 'typed' ? null : (row.option.group ?? null);
      let g = groups.find((x) => x.name === name);
      if (!g) { g = { name, rows: [] }; groups.push(g); }
      g.rows.push({ row, index });
    });
    return groups;
  }, [rows]);

  const selectable = (row: Row) => row.kind === 'typed' || !row.option.disabled;

  const openList = (withQuery = '') => {
    setQuery(withQuery);
    setActive(withQuery ? rows.findIndex(selectable) : Math.max(0, rows.findIndex((r) => r.kind === 'option' && r.option.value === value)));
    setOpen(true);
  };
  const closeList = () => { setOpen(false); setQuery(''); };
  const choose = (row: Row) => {
    if (!selectable(row)) return;
    if (row.kind === 'option') onChange(row.option.value);
    closeList();
  };
  const move = (dir: 1 | -1) => {
    if (rows.length === 0) return;
    let i = active;
    for (let step = 0; step < rows.length; step++) {
      i = (i + dir + rows.length) % rows.length;
      if (selectable(rows[i]!)) { setActive(i); return; }
    }
  };

  useLayoutEffect(() => {
    if (!open || !inputRef.current || !menuEl) return;
    const measure = () => {
      const anchor = inputRef.current;
      if (!anchor) return;
      const a = anchor.getBoundingClientRect();
      const m = menuEl.getBoundingClientRect();
      const pos = computePosition(a, { width: a.width, height: m.height }, { placement: 'bottom-start', offset: 4 });
      setCoords({ x: pos.x, y: pos.y, width: a.width });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, menuEl, rows.length]);

  useEffect(() => {
    if (!open || !menuRef.current) return;
    menuRef.current.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  useEffect(() => {
    if (active >= rows.length) setActive(Math.max(0, rows.length - 1));
  }, [rows.length, active]);

  useOnClickOutside(menuRef, (e) => {
    if (open && !inputRef.current?.parentElement?.contains(e.target as Node)) closeList();
  });

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (open) move(1); else openList(); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); if (open) move(-1); return; }
    if (!open) return;
    if (e.key === 'Enter') { e.preventDefault(); const row = rows[active]; if (row) choose(row); else closeList(); return; }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeList(); return; }
    if (e.key === 'Tab') closeList();
  };

  // ── what sits under the control ───────────────────────────────────────
  const depLabels = missing.map((k) => labels[k] ?? k);
  const named = depLabels.length <= 1
    ? depLabels.join('')
    : `${depLabels.slice(0, -1).join(', ')} ${t('settings.integrations.form.remote.and')} ${depLabels[depLabels.length - 1]}`;
  const regionBound = deps.includes('region');

  const status = (() => {
    if (!ready) return <span className="rr-remote-select__status">{t('settings.integrations.form.remote.fillFirst', { fields: named })}</span>;
    if (list.status === 'loading') {
      return (
        <span className="rr-remote-select__status rr-remote-select__status--loading">
          <Loader size={12} /> {t('settings.integrations.form.remote.loading')}
        </span>
      );
    }
    if (list.status !== 'ready') return null;
    const lines: React.ReactNode[] = [];
    if (value.trim()) {
      if (selected) {
        lines.push(
          <span key="sel" className="rr-remote-select__status">
            {selected.label}{selected.hint ? ` · ${selected.hint}` : ''}
          </span>
        );
      } else {
        lines.push(
          <span key="stale" className="rr-remote-select__status rr-remote-select__status--warn">
            {t(regionBound ? 'settings.integrations.form.remote.notFound' : 'settings.integrations.form.remote.typedNotListed', { value: value.trim() })}
          </span>
        );
      }
    }
    if (list.note) lines.push(<span key="note" className="rr-remote-select__status">{list.note}</span>);
    return lines.length ? <>{lines}</> : null;
  })();

  const error = list.status === 'error' ? (
    <>
      {t('settings.integrations.form.remote.loadFailed', { message: list.message })}{' '}
      <button type="button" className="rr-remote-select__retry" onClick={() => setAttempt((n) => n + 1)}>
        {t('settings.integrations.form.remote.retry')}
      </button>
    </>
  ) : undefined;

  const description = (
    <span className="rr-remote-select__under">
      {field.description ? <span>{field.description}</span> : null}
      {status}
    </span>
  );

  const activeId = rows[active] ? `${listId}-${active}` : undefined;

  const menu = open ? (
    <Portal>
      <div
        ref={(node) => { menuRef.current = node; setMenuEl(node); }}
        className="rr-remote-menu"
        style={{ left: coords?.x ?? -9999, top: coords?.y ?? -9999, width: coords?.width ?? 320 }}
        data-rr-remote-menu
      >
        {list.status === 'loading' && (
          <div className="rr-remote-menu__empty"><Loader size={12} /> {t('settings.integrations.form.remote.loading')}</div>
        )}
        {rows.length === 0 && list.status !== 'loading' && (
          <div className="rr-remote-menu__empty">
            {!ready ? t('settings.integrations.form.remote.fillFirst', { fields: named }) : t('settings.integrations.form.remote.noMatches')}
          </div>
        )}
        <ul id={listId} role="listbox" className="rr-remote-menu__list" aria-label={field.label}>
          {grouped.map((g) => (
            <li key={g.name ?? '__typed'} role="presentation" className="rr-remote-menu__section">
              {g.name ? <div className="rr-remote-menu__group" role="presentation">{g.name}</div> : null}
              <ul role="group" className="rr-remote-menu__list" aria-label={g.name ?? undefined}>
                {g.rows.map(({ row, index }) => row.kind === 'typed' ? (
                  <li
                    key="__typed"
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={index === active}
                    data-index={index}
                    data-active={index === active || undefined}
                    className="rr-remote-menu__option rr-remote-menu__option--typed"
                    onMouseEnter={() => setActive(index)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(row)}
                  >
                    <span className="rr-remote-menu__label">{t('settings.integrations.form.remote.useTyped', { value: row.value })}</span>
                    {list.status === 'ready' && <span className="rr-remote-menu__hint">{t('settings.integrations.form.remote.typedNotListed')}</span>}
                  </li>
                ) : (
                  <li
                    key={row.option.value}
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={row.option.value === value}
                    aria-disabled={row.option.disabled || undefined}
                    data-index={index}
                    data-active={index === active || undefined}
                    className="rr-remote-menu__option"
                    onMouseEnter={() => { if (!row.option.disabled) setActive(index); }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(row)}
                  >
                    <span className="rr-remote-menu__label">
                      {row.option.label}
                      {row.option.disabled ? <span className="rr-remote-menu__tag">{t('settings.integrations.form.remote.notSelectable')}</span> : null}
                    </span>
                    {row.option.value !== row.option.label && <span className="rr-remote-menu__value">{row.option.value}</span>}
                    {row.option.hint ? <span className="rr-remote-menu__hint">{row.option.hint}</span> : null}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </Portal>
  ) : null;

  const chevron = (
    <button
      type="button"
      className="rr-remote-select__chevron"
      aria-label={t('settings.integrations.form.remote.open')}
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { if (open) closeList(); else { inputRef.current?.focus(); openList(); } }}
    >
      {list.status === 'loading' ? <Loader size={12} /> : (
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="1,1 5,5 9,1" />
        </svg>
      )}
    </button>
  );

  return (
    <>
      <InputWrapper
        label={field.label}
        description={description}
        error={error}
        required={!!field.required}
        rightSection={chevron}
        rightSectionWidth={28}
        className="rr-remote-select"
      >
        {({ inputId, describedBy, invalid, className }) => (
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            className={`${className} rr-remote-select__input`}
            value={value}
            placeholder={t('settings.integrations.form.remote.search')}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-activedescendant={open ? activeId : undefined}
            onChange={(e) => { onChange(e.currentTarget.value); setQuery(e.currentTarget.value); if (!open) setOpen(true); setActive(0); }}
            onFocus={() => { if (!open) openList(); }}
            onClick={() => { if (!open) openList(); }}
            onKeyDown={onKey}
          />
        )}
      </InputWrapper>
      {menu}
    </>
  );
}
